/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Button,
	Stack,
} from '@mantine/core';

// Components
import { Tooltip } from '@components/atoms/Tooltip';

// Style
import classes from './DrawerMeterPointShowMore.module.css';

// Utils
import { truncateString } from '@utils/functions';

// Icons
import ProductionLogo from '@assets/images/production-logo.svg';
import { IconEdit, IconTrash } from '@tabler/icons-react';

// I18n
import { useTranslation } from 'react-i18next';

// Type
import { TParsedMeterPoint } from '../../types';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type DrawerMeterPointShowMoreProps = {
	onClose: () => void;
	handledelete?: () => void;
	handleupdate?: () => void;
	opened: boolean;
	data: Partial<TParsedMeterPoint>;
	position: 'right' | 'left';
	fromstepper?: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerMeterPointShowMore = (
	props: DrawerMeterPointShowMoreProps
) => {
	const {
		opened,
		onClose,
		handledelete,
		fromstepper,
		data: meterPoint,
		handleupdate,
		...restProps
	} = props;

	const {
		organization,
		producer,
		project,
		site,
		serialNumber,
		place,
		unit,
		creationDate,
	} = meterPoint;
	/* ********************************  HOOKS  ********************************* */
	const { checkAcl, checkRole } = useAuthorization();
	const { t } = useTranslation();
	/* ******************************** RENDERING ******************************* */
	if (meterPoint)
		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={ProductionLogo}
						/>
					</Box>
					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.site')}
						</Text>
						{props.data.project && (
							<Text className={classes.customTitle}>
								{props.data.site}
							</Text>
						)}
					</Box>
				</Box>
				{/* Body */}

				<Box className={classes.bodyDrawer}>
					{organization && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.organization')}
							</Text>
							<Tooltip
								label={organization}
								isActive={organization.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(organization, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{producer && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.producer')}
							</Text>
							<Tooltip
								label={producer}
								isActive={producer.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(producer, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{project && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.project')}
							</Text>
							<Tooltip
								label={project}
								isActive={project.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(project, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{site && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.site')}
							</Text>
							<Tooltip
								label={site}
								isActive={site.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(site, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{serialNumber && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.serialNumber')}
							</Text>
							<Tooltip
								label={serialNumber}
								isActive={serialNumber.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(serialNumber, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{place && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.place')}
							</Text>
							<Tooltip
								label={place}
								isActive={place.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(place, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{unit && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.unit')}
							</Text>
							<Tooltip
								label={unit}
								isActive={unit.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(unit, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{creationDate && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.creationDate')}
							</Text>
							<Tooltip
								label={creationDate}
								isActive={creationDate.length > 20}
							>
								<Text className={classes.value}>
									{truncateString(creationDate, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}
				</Box>

				{/* Footer */}
				{!fromstepper && (
					<Stack gap={'10px'} className={classes.footer}>
						{checkAcl(ACLS.METER_POINTS.UPDATE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<div>
									<Button
										onClick={handleupdate}
										leftSection={<IconEdit size={16} />}
										hiddenFrom="xxl"
										size="sm"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('meterPoints.updateMeterPoint')}
									</Button>
									<Button
										onClick={handleupdate}
										leftSection={<IconEdit size={20} />}
										size="md"
										visibleFrom="xxl"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('meterPoints.updateMeterPoint')}
									</Button>
								</div>
							)}
						{checkAcl(ACLS.METER_POINTS.DELETE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<div>
									<Button
										onClick={handledelete}
										leftSection={<IconTrash size={16} />}
										hiddenFrom="xxl"
										size="sm"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('meterPoints.deleteMeterPoint')}
									</Button>
									<Button
										onClick={handledelete}
										leftSection={<IconTrash size={20} />}
										size="md"
										visibleFrom="xxl"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('meterPoints.deleteMeterPoint')}
									</Button>
								</div>
							)}
					</Stack>
				)}
			</NativeDrawer>
		);
};
